// Sticky timeline band of TTSView: optional over-limit pill (flex assets),
// the Timeline with its mixer slot, and the ExportBar. Owns the mixer
// popover's open state (it was the only consumer in TTSView).
//
// `stickyRef` stays owned by TTSView — its ParamPanel scroll-into-view
// logic reads the band's rect.

import { useCallback, useRef, useState, type RefObject } from 'react';
import type { Block as BlockT } from '@shared/types';
import { Timeline } from '@/components/Timeline/Timeline';
import { ExportBar } from '@/components/Timeline/ExportBar';
import { MixerButton } from '@/components/AudioMixer/MixerButton';
import { MixerPopover } from '@/components/AudioMixer/MixerPopover';
import { OverLimitPill } from '@/components/OverLimitPill/OverLimitPill';
import styles from '../TTSView.module.css';

interface TtsTimelineBandProps {
  stickyRef: RefObject<HTMLDivElement | null>;
  /** Flex asset (Multi-Lingual ohne Video): over-limit pill, no mixer, no
   *  overflow shade. */
  flexAsset: boolean;
  blocks: BlockT[];
  limitSec: number | null | undefined;
  onBlocksReordered: (indexMap: Map<number, number>) => void;
  onResetPositions: () => void;
}

export function TtsTimelineBand({
  stickyRef,
  flexAsset,
  blocks,
  limitSec,
  onBlocksReordered,
  onResetPositions,
}: TtsTimelineBandProps) {
  // Mixer popover open/close + button ref (for outside-click). The popover
  // itself reads/writes the mixer store; this band just owns its visibility.
  const [mixerOpen, setMixerOpen] = useState(false);
  const mixerBtnRef = useRef<HTMLDivElement | null>(null);
  const toggleMixer = useCallback((ev: React.MouseEvent) => {
    ev.stopPropagation();
    setMixerOpen((o) => !o);
  }, []);
  const closeMixer = useCallback(() => setMixerOpen(false), []);

  return (
    <div
      ref={stickyRef}
      className={styles.timelineSticky}
      data-sticky-push="header"
      data-coach="timeline"
    >
      {/* AssetLengthControl moved up to Generation/index.tsx (FB3#7 —
          2026-04-28); the now-empty .timelineHeader strip was removed
          here so the Timeline sits flush against the sticky band's top
          padding. */}
      {flexAsset && (
        <OverLimitPill
          chips={blocks
            .filter((b) => b.favIdx >= 0 && b.takes[b.favIdx])
            .map((b) => ({ offset: b.offset ?? 0, dur: b.takes[b.favIdx].duration ?? 0 }))}
          limitSec={limitSec}
        />
      )}
      <Timeline
        mixerSlot={
          flexAsset ? undefined : (
            <div ref={mixerBtnRef} className={styles.mixerAnchor}>
              <MixerButton isOpen={mixerOpen} onToggle={toggleMixer} />
              <MixerPopover
                isOpen={mixerOpen}
                onClose={closeMixer}
                buttonRef={mixerBtnRef}
              />
            </div>
          )
        }
        hideOverflowShade={flexAsset}
        onBlocksReordered={onBlocksReordered}
      />
      <ExportBar onResetPositions={onResetPositions} />
    </div>
  );
}
